import { z } from 'zod'

// Every schema here validates one JSON shape the web app reads from or sends
// to apps/api. Response objects use z.object (not z.strictObject) so unknown
// keys the API adds later are silently stripped rather than rejected.

// --- audit / seo ---
// Mirrors apps/api/audit/types.go and apps/api/seo/types.go — both report
// shapes are identical, so the web app uses a single set of schemas.

const CheckStatusSchema = z.enum(['pass', 'warn', 'fail'])

const CheckSchema = z.object({
  key: z.string(),
  title: z.string(),
  status: CheckStatusSchema,
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
})

const SummarySchema = z.object({
  total: z.number(),
  passed: z.number(),
  warned: z.number(),
  failed: z.number(),
  score: z.number(),
})

const ReportSchema = z.object({
  url: z.string(),
  host: z.string(),
  timestamp: z.string(),
  duration_ns: z.number(),
  summary: SummarySchema,
  checks: z.array(CheckSchema),
})
export type Report = z.infer<typeof ReportSchema>

export const AuditResponseSchema = z.object({
  report: ReportSchema,
  audit_id: z.number(),
})
export type AuditResponse = z.infer<typeof AuditResponseSchema>

export const FollowupResponseSchema = z.object({
  ok: z.boolean(),
})
export type FollowupResponse = z.infer<typeof FollowupResponseSchema>

export const ApiErrorSchema = z.object({
  error: z.string(),
})

/** Coarse grade derived from the score — mirrors Summary.Letter() in Go. */
export function letterGrade(score: number): string {
  if (score >= 90) return 'A'
  if (score >= 80) return 'B'
  if (score >= 70) return 'C'
  if (score >= 60) return 'D'
  return 'F'
}

// --- auth (apps/api/auth/auth.go Account) ---
// Role is a closed set enforced server-side by SetRole (auth.go): only
// "member" or "admin" are ever written, so this is a real enum.

export const AccountSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.enum(['member', 'admin']),
  permissions: z.array(z.string()),
  authentication_methods: z.array(z.string()),
})
export type Account = z.infer<typeof AccountSchema>

// --- admin intake definitions/list/create (apps/api/intake/admin.go) ---

const AdminDefinitionSchema = z.object({
  id: z.string(),
  title: z.string(),
  steps: z.number(),
  arranged_facts: z.record(z.string(), z.unknown()),
})
export type AdminDefinition = z.infer<typeof AdminDefinitionSchema>

export const AdminDefinitionsResponseSchema = z.object({
  definitions: z.array(AdminDefinitionSchema),
})
export type AdminDefinitionsResponse = z.infer<typeof AdminDefinitionsResponseSchema>

// Status is computed server-side in admin.go's adminIntake() from exactly
// these four values (the stored "active"/"submitted" plus derived
// "revoked"/"expired"), so it's a closed set.
const AdminIntakeStatusSchema = z.enum(['active', 'expired', 'revoked', 'submitted'])

export const AdminIntakeSchema = z.object({
  id: z.number(),
  client_name: z.string(),
  form_type: z.string(),
  status: AdminIntakeStatusSchema,
  created_at: z.string(),
  expires_at: z.string(),
  url: z.string(),
  // Active files only; missing_required holds labels of required slots with no file.
  documents: z.object({ file_count: z.number(), missing_required: z.array(z.string()) }),
})
export type AdminIntake = z.infer<typeof AdminIntakeSchema>

export const AdminIntakePageSchema = z.object({
  intakes: z.array(AdminIntakeSchema),
  page: z.number(),
  page_size: z.number(),
  total: z.number(),
})
export type AdminIntakePage = z.infer<typeof AdminIntakePageSchema>

// --- admin intake detail (apps/api/intake/admin.go AdminIntakeDetail) ---

// Data is the step's saved JSON with sensitive values stripped server-side.
// Its shape varies by step, so the admin view renders it generically.
const AdminIntakeResponseSchema = z.object({
  step: z.number(),
  title: z.string(),
  data: z.unknown(),
  updated_at: z.string(),
})

const AdminIntakeFileSchema = z.object({
  kind: z.string(),
  filename: z.string(),
  content_type: z.string(),
  size: z.number(),
  uploaded_at: z.string(),
})

const AdminIntakeEventSchema = z.object({
  event: z.string(),
  detail: z.string(),
  created_at: z.string(),
})

export const AdminIntakeDetailSchema = z.object({
  intake: AdminIntakeSchema,
  first_opened_at: z.string().nullable(),
  last_activity_at: z.string().nullable(),
  submitted_at: z.string().nullable(),
  responses: z.array(AdminIntakeResponseSchema),
  files: z.array(AdminIntakeFileSchema),
  // Newest first, capped server-side.
  events: z.array(AdminIntakeEventSchema),
})
export type AdminIntakeDetail = z.infer<typeof AdminIntakeDetailSchema>

// --- intake state (apps/api/intake/types.go) ---

const IntakeProgressSchema = z.object({
  total_steps: z.number(),
  saved_steps: z.array(z.number()),
  saved_count: z.number(),
  next_step: z.number(),
})
export type IntakeProgress = z.infer<typeof IntakeProgressSchema>

// Data is json.RawMessage server-side — the original JSON object for that
// step. Its structure depends on which step it is, so per-step schemas in
// views/intake/formDefinitions.ts parse it further before use.
const IntakeResponseSchema = z.object({
  step: z.number(),
  data: z.record(z.string(), z.unknown()),
  updated_at: z.string(),
})

const IntakeDocumentSchema = z.object({
  id: z.number(),
  kind: z.string(),
  filename: z.string(),
  content_type: z.string(),
  size: z.number(),
  uploaded_at: z.string(),
})
export type IntakeDocument = z.infer<typeof IntakeDocumentSchema>

// A definition's service checklist; only the Burns definition sends one.
const IntakeServiceGroupSchema = z.object({
  title: z.string(),
  source: z.string(),
  services: z.array(z.object({ id: z.string(), label: z.string() })),
})
export type IntakeServiceGroup = z.infer<typeof IntakeServiceGroupSchema>

// One slot in the server-owned document checklist (documentCatalog in
// apps/api/intake/definitions.go). Aliases are legacy kinds that still count
// toward the slot; step is the 0-based step it renders on.
const IntakeDocumentSlotSchema = z.object({
  kind: z.string(),
  label: z.string(),
  need: z.enum(['required', 'recommended', 'if_applicable']),
  help: z.string(),
  source_url: z.string(),
  aliases: z.array(z.string()),
  step: z.number(),
})
export type IntakeDocumentSlot = z.infer<typeof IntakeDocumentSlotSchema>

const IntakeDefinitionSchema = z.object({
  id: z.string(),
  defaults: z.record(z.string(), z.unknown()),
  services: z.array(IntakeServiceGroupSchema).optional(),
  documents: z.array(IntakeDocumentSlotSchema),
})

export const IntakeStateSchema = z.object({
  client_name: z.string(),
  form_type: z.string(),
  definition: IntakeDefinitionSchema,
  progress: IntakeProgressSchema,
  responses: z.array(IntakeResponseSchema),
  documents: z.array(IntakeDocumentSchema),
  warnings: z.array(z.string()),
  // A submitted link stays open for document uploads and removals only.
  submitted: z.boolean(),
  // False when the server has no address lookup configured.
  address_suggestions: z.boolean().optional(),
})
export type IntakeState = z.infer<typeof IntakeStateSchema>

/** One address autocomplete row; pass place_id back to getIntakeAddress for the parts. */
const IntakeAddressSuggestionSchema = z.object({
  place_id: z.string(),
  main: z.string(),
  secondary: z.string(),
})
export type IntakeAddressSuggestion = z.infer<typeof IntakeAddressSuggestionSchema>

export const IntakeAddressSuggestionsSchema = z.object({
  suggestions: z.array(IntakeAddressSuggestionSchema),
})

/** A US address in parts: state is the two-letter code, zip is 5 digits or ZIP+4. */
export const IntakeAddressSchema = z.object({
  street: z.string().optional(),
  unit: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
})
export type IntakeAddress = z.infer<typeof IntakeAddressSchema>

export const IntakeSaveResultSchema = z.object({
  step: z.number(),
  updated_at: z.string(),
  progress: IntakeProgressSchema,
})
export type IntakeSaveResult = z.infer<typeof IntakeSaveResultSchema>

export const IntakeUploadResultSchema = z.object({
  document_id: z.number(),
  filename: z.string(),
  content_type: z.string(),
  size: z.number(),
})
export type IntakeUploadResult = z.infer<typeof IntakeUploadResultSchema>
